import { useState, useCallback } from 'react';
import api from '../../../api';
import { parseErrorDetail } from '../utils/portHelpers';
import { isServiceNumber } from '../../../shared/portActivation';

/**
 * Hook for managing port form state and submission
 */
export function usePortForm(ports, setPorts, setError, setSuccess) {
  const [editingPort, setEditingPort] = useState(null);
  const [formData, setFormData] = useState({
    gateway_id: '',
    port_number: '',
    number_source: 'gateway',
    assigned_number: '',
    user_code: '',
    user_email: '',
    slack_user_id: '',
    description: '',
    credit_balance: '',
    last_message_sent: '',
    is_sim_converted: '1',
    sim_converted_note: '',
  });

  // Reset form back to Add New Port mode
  const resetForm = useCallback(() => {
    setFormData((prev) => {
      return {
        gateway_id: prev.gateway_id || '',
        port_number: '',
        number_source: 'gateway',
        assigned_number: '',
        user_code: '',
        user_email: '',
        slack_user_id: '',
        description: '',
        credit_balance: '',
        last_message_sent: '',
        is_sim_converted: '1',
        sim_converted_note: '',
      };
    });
    setEditingPort(null);
    setError(null);
    setSuccess(null);
  }, [setError, setSuccess]);

  // Form change handler
  const handleInputChange = useCallback(
    (event) => {
      const { name, value } = event.target;

      setFormData((prevData) => {
        const updatedFormData = {
          ...prevData,
          [name]: value,
        };

        if (name === 'number_source' && value === 'service') {
          updatedFormData.gateway_id = '';
          updatedFormData.port_number = '';
          updatedFormData.is_sim_converted = '1';
          updatedFormData.sim_converted_note = '';
        }

        // auto-fill Slack User ID and email from other numbers for this user
        if (name === 'user_code' && value) {
          const matchingPorts = ports.filter(
            (port) =>
              (port.user_code || '').trim().toLowerCase() ===
              String(value).trim().toLowerCase()
          );
          const slacks = [
            ...new Set(
              matchingPorts
                .map((port) => (port.slack_user_id || '').trim())
                .filter(Boolean)
            ),
          ];
          const emails = [
            ...new Set(
              matchingPorts
                .map((port) => (port.user_email || '').trim())
                .filter(Boolean)
            ),
          ];
          updatedFormData.slack_user_id =
            slacks.length === 1 ? slacks[0] : matchingPorts[0]?.slack_user_id || '';
          if (!updatedFormData.user_email && emails.length === 1) {
            updatedFormData.user_email = emails[0];
          }
        }

        return updatedFormData;
      });
    },
    [ports]
  );

  // Create/update submit
  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const payload = { ...formData };

    if (
      typeof payload.credit_balance === 'string' &&
      payload.credit_balance.trim() === ''
    ) {
      delete payload.credit_balance;
    }

    if (
      typeof payload.last_message_sent === 'string' &&
      payload.last_message_sent.trim() === ''
    ) {
      delete payload.last_message_sent;
    } else if (payload.last_message_sent) {
      // Convert date string to ISO date format (YYYY-MM-DD)
      // The backend will accept this and convert to datetime internally
      const dateStr = payload.last_message_sent;
      // If it's already in YYYY-MM-DD format, use it directly
      // Otherwise parse and format it
      if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
        payload.last_message_sent = dateStr;
      } else {
        payload.last_message_sent = new Date(dateStr).toISOString().split('T')[0];
      }
    }

    if (
      payload.is_sim_converted === '' ||
      payload.is_sim_converted === null ||
      payload.is_sim_converted === undefined
    ) {
      payload.is_sim_converted = 1;
    } else {
      payload.is_sim_converted = Number(payload.is_sim_converted);
    }

    if (
      typeof payload.sim_converted_note === 'string' &&
      payload.sim_converted_note.trim() === ''
    ) {
      payload.sim_converted_note = null;
    }

    payload.number_source = payload.number_source || 'gateway';
    if (payload.number_source === 'service') {
      payload.gateway_id = null;
      payload.port_number = null;
      payload.is_sim_converted = 1;
      payload.sim_converted_note = null;
    } else if (payload.gateway_id === '') {
      payload.gateway_id = null;
    }

    if (!editingPort) {
      delete payload.is_sim_converted;
      delete payload.sim_converted_note;
    }

    try {
      if (editingPort) {
        const response = await api.put(`/ports/${editingPort.id}`, payload);
        if (response.data) {
          setSuccess('Port updated successfully');
          setPorts((prevPorts) =>
            prevPorts.map((port) =>
              port.id === editingPort.id ? response.data : port
            )
          );
          resetForm();
        }
      } else {
        const response = await api.post('/ports/', payload);
        if (response.data) {
          setSuccess('Port created successfully');
          setPorts((prevPorts) => [...prevPorts, response.data]);
          resetForm();
        }
      }
    } catch (err) {
      console.error('Error saving port:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const detail = err.response?.data?.detail;
        setError(parseErrorDetail(detail));
      }
    }
  };

  // Edit existing row
  const handleEdit = useCallback((port) => {
    setEditingPort(port);

    const gatewayForSelect = port.gateway_id ?? '';

    setFormData({
      gateway_id: gatewayForSelect || '',
      port_number: port.port_number || '',
      number_source: port.number_source || 'gateway',
      assigned_number: port.assigned_number,
      user_code: port.user_code || '',
      user_email: port.user_email || '',
      slack_user_id: port.slack_user_id || '',
      description: port.description || '',
      credit_balance: port.credit_balance || '',
      last_message_sent: port.last_message_sent
        ? new Date(port.last_message_sent).toISOString().split('T')[0]
        : '',
      is_sim_converted:
        port.is_sim_converted === null ||
        port.is_sim_converted === undefined ||
        port.is_sim_converted === 0
          ? '1'
          : String(port.is_sim_converted),
      sim_converted_note: port.sim_converted_note || '',
    });

    setError(null);
    setSuccess(null);
  }, [setError, setSuccess]);

  // Delete row
  const handleDelete = async (portId, setPorts, setError, setSuccess) => {
    if (!window.confirm('Are you sure you want to delete this port?')) return;

    try {
      const response = await api.delete(`/ports/${portId}`);
      if (response.data) {
        setSuccess('Port deleted successfully');
        setPorts((prevPorts) =>
          prevPorts.filter((port) => port.id !== portId)
        );
      }
    } catch (err) {
      console.error('Error deleting port:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const detail = err.response?.data?.detail;
        setError(parseErrorDetail(detail));
      }
    }
  };

  // Archive row (mark as replaced/no longer in use)
  const handleArchive = useCallback(async (portId) => {
    const target = (ports || []).find((p) => p.id === portId);
    const archiveMsg = isServiceNumber(target)
      ? 'Archive this service number? It will be marked as no longer in use. There is no gateway slot to free.'
      : 'Archive this number? It will be marked as "No longer in use" in the Advice view but preserved for staff records. The port slot will be freed for a replacement SIM.';
    if (!window.confirm(archiveMsg)) return;

    try {
      const response = await api.post(`/ports/${portId}/archive`);
      if (response.data) {
        setSuccess('Port archived successfully');
        setPorts((prevPorts) =>
          prevPorts.map((port) =>
            port.id === portId
              ? { ...port, archived: true, archived_at: new Date().toISOString(), assigned: false }
              : port
          )
        );
      }
    } catch (err) {
      console.error('Error archiving port:', err);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        const detail = err.response?.data?.detail;
        setError(parseErrorDetail(detail));
      }
    }
  }, [ports, setPorts, setError, setSuccess]);

  return {
    formData,
    editingPort,
    setFormData,
    handleInputChange,
    handleSubmit,
    handleEdit,
    handleDelete: (portId) => handleDelete(portId, setPorts, setError, setSuccess),
    handleArchive,
    resetForm,
  };
}


